import DownloadIcon from '@mui/icons-material/Download'
import {
  Button,
  Card,
  CardContent,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import { useState } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { api } from '@/api/client'
import { useFinanceDashboard } from '@/api/hooks'
import {
  EmptyState,
  ErrorBanner,
  formatDate,
  formatMoney,
  formatNumber,
  humanise,
  InlineSpinner,
  isoDate,
  MetricCard,
  PageHeader,
} from '@/components/common'
import { useChartTokens } from '@/theme'



export function FinanceDashboardPage() {
  const theme = useTheme()
  const chart = useChartTokens()
  // Opens on today. Every list and every report starts on the day being
  // worked, and the range is widened when somebody wants history.
  const [from, setFrom] = useState(() => isoDate(0))
  const [to, setTo] = useState(() => isoDate(0))
  const [trendPeriod, setTrendPeriod] = useState<'weeklyTrend' | 'monthlyTrend' | 'yearlyTrend'>(
    'monthlyTrend',
  )
  const { data, isLoading, error } = useFinanceDashboard(from, to)

  return (
    <>
      <PageHeader
        title="Finance dashboard"
        subtitle="Billing, collection, ageing and event profitability"
        actions={
          <Stack direction="row" spacing={1}>
            <TextField
              label="From"
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="To"
              type="date"
              value={to}
              onChange={(event) => setTo(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() =>
                api.download('/api/v1/reports/CUSTOMER_OUTSTANDING/export', { from, to },
                  'customer_outstanding.xlsx')
              }
            >
              Outstanding
            </Button>
            {/* Two tables of the form, so two workbooks. The rest of the
                reports live on the Reports page; these are here because the
                date range is already in hand. */}
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() =>
                api.download('/api/v1/reports/GST_ADVANCES_11A/export', { from, to },
                  `gst_11a_${from}_to_${to}.xlsx`)
              }
            >
              GST 11A
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() =>
                api.download('/api/v1/reports/GST_ADVANCES_11B/export', { from, to },
                  `gst_11b_${from}_to_${to}.xlsx`)
              }
            >
              GST 11B
            </Button>
          </Stack>
        }
      />

      <ErrorBanner error={error} />
      {isLoading && <InlineSpinner />}

      {data && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard label="Total billed" value={formatMoney(data.totalBilled)} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard label="Collected" value={formatMoney(data.totalCollected)} tone="success" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard label="Advance expected" value={formatMoney(data.advanceReceived)} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                label="Outstanding"
                value={formatMoney(data.outstandingAmount)}
                tone={data.outstandingAmount > 0 ? 'warning' : 'success'}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard label="Pending invoices" value={formatNumber(data.pendingInvoices)} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                label="Overdue invoices"
                value={formatNumber(data.overdueInvoices)}
                tone={data.overdueInvoices > 0 ? 'error' : 'success'}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard label="Discounts given" value={formatMoney(data.discountsGiven)} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard label="Refunds processed" value={formatMoney(data.refundsProcessed)} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard label="Complimentary given" value={formatMoney(data.complimentaryGiven)} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard label="Write-offs" value={formatMoney(data.writeOffs)} />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Receivables ageing
                  </Typography>
                  {data.ageingBuckets.every((bucket) => bucket.amount === 0) ? (
                    <EmptyState message="Nothing outstanding. Every invoice is settled." />
                  ) : (
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={data.ageingBuckets}>
                        <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                        <XAxis dataKey="name" fontSize={12} stroke={chart.axis} />
                        <YAxis
                          tickFormatter={(value) => `${Math.round(value / 1000)}k`}
                          fontSize={12}
                          stroke={chart.axis}
                        />
                        <Tooltip
                          formatter={(value: number) => formatMoney(value)}
                          contentStyle={chart.tooltip}
                          labelStyle={{ color: chart.tooltip.color }}
                          cursor={{ fill: chart.grid }}
                        />
                        {/* Money still owed reads as a warning, not as brand colour. */}
                        <Bar
                          dataKey="amount"
                          name="Outstanding"
                          fill={theme.palette.warning.main}
                          radius={[6, 6, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="subtitle1">Revenue trend</Typography>
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={trendPeriod}
                      onChange={(_event, value) => value && setTrendPeriod(value)}
                    >
                      <ToggleButton value="weeklyTrend">Weekly</ToggleButton>
                      <ToggleButton value="monthlyTrend">Monthly</ToggleButton>
                      <ToggleButton value="yearlyTrend">Yearly</ToggleButton>
                    </ToggleButtonGroup>
                  </Stack>
                  {data[trendPeriod].length === 0 ? (
                    <EmptyState message="No events billed in this period." />
                  ) : (
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={data[trendPeriod]}>
                        <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                        <XAxis
                          dataKey="date"
                          tickFormatter={(value) => formatDate(value)}
                          fontSize={12}
                          stroke={chart.axis}
                        />
                        <YAxis
                          tickFormatter={(value) => `${Math.round(value / 1000)}k`}
                          fontSize={12}
                          stroke={chart.axis}
                        />
                        <Tooltip
                          formatter={(value: number) => formatMoney(value)}
                          labelFormatter={(value: string) => formatDate(value)}
                          contentStyle={chart.tooltip}
                          labelStyle={{ color: chart.tooltip.color }}
                          cursor={{ fill: chart.grid }}
                        />
                        <Bar dataKey="amount" name="Revenue" fill={chart.series[0]} radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Billed by service
                  </Typography>
                  {data.billedByService.length === 0 ? (
                    <EmptyState message="No events billed in this period." />
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Service</TableCell>
                          <TableCell align="right">Events</TableCell>
                          <TableCell align="right">Revenue</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.billedByService.map((row) => (
                          <TableRow key={row.name} hover>
                            <TableCell>{humanise(row.name)}</TableCell>
                            <TableCell align="right">{formatNumber(row.count)}</TableCell>
                            <TableCell align="right">{formatMoney(row.amount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Billed by branch
                  </Typography>
                  {data.billedByOutlet.length === 0 ? (
                    <EmptyState message="No events billed in this period." />
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Branch</TableCell>
                          <TableCell align="right">Events</TableCell>
                          <TableCell align="right">Revenue</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.billedByOutlet.map((row) => (
                          <TableRow key={row.name} hover>
                            <TableCell>{row.name}</TableCell>
                            <TableCell align="right">{formatNumber(row.count)}</TableCell>
                            <TableCell align="right">{formatMoney(row.amount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}
    </>
  )
}
